import { useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { FileUp, NotebookPen, Trash2 } from "lucide-react";
import { useState } from "react";

import { api } from "../../api/client";
import type { Retex, RetexEnemy, RetexEntry, RetexGroup } from "../../api/types";
import { Banner } from "../../components/Callout";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { DropZone, useFileDrop } from "../../components/DropZone";
import { EmptyState } from "../../components/EmptyState";
import { motion, Presence } from "../../components/presence";
import { Badge, Button, IconButton } from "../../components/ui";
import { ViewHeader } from "../../components/ViewHeader";
import { locale, t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { useUi } from "../../state/ui";
import { briefingKey } from "../briefing/briefingJob";
import { DossierEmptyState } from "../DossierEmptyState";
import { eniLabel, RetexImportDialog } from "./RetexImportDialog";
import { useWriteBlock } from "../../sharing/access";

/** RETEX (Documents) : le scanner RETEX de l'iPad, sans caméra. Les comptes rendus d'observation de l'ennemi
 *  se versent au RETEX de l'un des cinq ennemis doctrinaux (cinq au plus, 1 500 caractères chacun) ; ceux de
 *  l'ennemi joué complètent sa description quand le modèle rédige la situation géopolitique de la mise en
 *  ambiance (3 000 caractères au plus, les plus récents d'abord). */

const when = (iso: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return t("le {date} à {time}", {
    date: d.toLocaleDateString(locale(), { day: "2-digit", month: "2-digit", year: "2-digit" }),
    time: d.toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" }),
  });
};

function EntryCard({
  entry,
  played,
  threshold,
  onDelete,
}: {
  entry: RetexEntry;
  played: boolean;
  threshold: number;
  onDelete: () => void;
}) {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const [open, setOpen] = useState(false);
  const long = entry.text.length > 420;
  return (
    <li className={clsx("px-4 py-3", motion("fade"))}>
      <p className={clsx("text-left text-title leading-relaxed whitespace-pre-line text-ink hyphens-auto sm:text-justify", !open && long && "line-clamp-5")}>
        {entry.text}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-meta text-ink-muted">
        <span className="max-w-[260px] truncate font-medium text-ink-soft" title={tServer(entry.source)}>
          {tServer(entry.source)}
        </span>
        <span>
          {when(entry.created_at)}
          {entry.created_by ? ` · ${entry.created_by}` : ""}
        </span>
        <span
          className={clsx("tabular-nums", entry.score >= threshold ? "text-ok" : "text-ink-muted")}
          title={t("Score de pertinence pour cet ennemi : 6 ou plus pour un mode d'action ennemi")}
        >
          {t("score {score}", { score: entry.score.toLocaleString(locale()) })}
        </span>
        {played &&
          (entry.in_briefing ? (
            <Badge tone="accent" title={t("Ajouté à la description de l'ennemi pour rédiger la situation géopolitique")}>
              {t("Sert à la rédaction")}
            </Badge>
          ) : (
            <Badge title={t("Au-delà des 3 000 caractères confiés au modèle : les comptes rendus les plus récents passent d'abord")}>
              {t("Hors limite")}
            </Badge>
          ))}
        {long && (
          <Button variant="link" onClick={() => setOpen((o) => !o)}>
            {open ? t("Réduire") : t("Lire tout")}
          </Button>
        )}
        <IconButton
          size="sm"
          variant="danger"
          className="ml-auto"
          onClick={onDelete}
          {...writeBlock}
          title={t("Retirer ce compte rendu du RETEX")}
          aria-label={t("Retirer ce compte rendu du RETEX")}
        >
          <Trash2 size={14} />
        </IconButton>
      </div>
    </li>
  );
}

function GroupCard({
  group,
  retex,
  onDelete,
  onClear,
  onImport,
}: {
  group: RetexGroup;
  retex: Retex;
  onDelete: (entry: RetexEntry) => void;
  onClear: () => void;
  onImport: () => void;
}) {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const feeds = group.played && retex.feeds_briefing;
  return (
    <section className="overflow-hidden rounded-xl border border-line bg-surface shadow-card">
      <header className="flex min-h-11 flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <h2 className="text-title font-semibold text-ink">{eniLabel(group.eni, retex.custom)}</h2>
        {group.played && <Badge tone="friend">{t("Ennemi du dossier")}</Badge>}
        <span className="text-meta text-ink-muted tabular-nums">
          {group.count} / {retex.limits.entries}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {group.count > 0 && (
            <Button size="sm" variant="danger-ghost" onClick={onClear} {...writeBlock}>
              <Trash2 size={14} /> {t("Tout effacer")}
            </Button>
          )}
        </div>
      </header>
      {/* Rôle du RETEX de l'ennemi joué : état durable de la carte, en bandeau (R10). */}
      <Presence show={group.played}>
        {group.played && (
        <Banner tone="neutral">
          {feeds
            ? t(
                "Ces comptes rendus complètent la description de l'{eni} quand le modèle rédige la situation géopolitique de la mise en ambiance ({n} caractères au plus, les plus récents d'abord).",
                { eni: eniLabel(group.eni, retex.custom), n: retex.limits.prompt.toLocaleString(locale()) },
              )
            : t("En contre-rébellion, la situation géopolitique décrit le groupe armé : le RETEX de l'ennemi n'y sert pas.")}
        </Banner>
        )}
      </Presence>
      {group.count === 0 ? (
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
          <p className="text-ui text-ink-muted">{t("Aucun compte rendu sur l'{eni} dans ce dossier.", { eni: eniLabel(group.eni, retex.custom) })}</p>
          <Button size="sm" variant="secondary" onClick={onImport} {...writeBlock}>
            <FileUp size={14} /> {t("Importer")}
          </Button>
        </div>
      ) : (
        <ul className="divide-y divide-line">
          {group.entries.map((entry) => (
            <EntryCard key={entry.id} entry={entry} played={feeds} threshold={retex.limits.threshold} onDelete={() => onDelete(entry)} />
          ))}
        </ul>
      )}
    </section>
  );
}

export function RetexView() {
  // Dossier en lecture seule pour le compte (docs/17 §3.2) : écritures bloquées, l'appui désigne le bandeau.
  const writeBlock = useWriteBlock();
  const exerciseId = useUi((s) => s.exerciseId);
  const notify = useUi((s) => s.notify);
  const setView = useUi((s) => s.setView);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const key = ["retex", exerciseId] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => api.retex(exerciseId as string),
    enabled: Boolean(exerciseId && exercise),
  });
  const data = query.data;
  // Force non française (ennemi générique, registre B39) : RETEX inactif, rien à importer ni à montrer.
  const unavailable = data?.available === false;
  const [importing, setImporting] = useState(false);
  const [dropped, setDropped] = useState<File | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "entry"; entry: RetexEntry } | { kind: "all"; eni: RetexEnemy } | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = Boolean(exerciseId && exercise);

  const apply = (next: Retex) => {
    queryClient.setQueryData(key, next);
    void queryClient.invalidateQueries({ queryKey: briefingKey(exerciseId) });
  };
  const openImport = (file: File | null = null) => {
    setDropped(file);
    setImporting(true);
  };
  const total = data?.enemies.reduce((n, g) => n + g.count, 0) ?? 0;
  // Ennemi joué d'abord, puis ceux qui ont un RETEX ; les autres sont nommés à la fin.
  const shown = data ? data.enemies.filter((g) => g.played || g.count > 0).sort((a, b) => Number(b.played) - Number(a.played)) : [];
  const empty = data ? data.enemies.filter((g) => !g.played && g.count === 0) : [];

  const remove = async () => {
    if (!confirm || !exerciseId) return;
    setBusy(true);
    try {
      const next =
        confirm.kind === "entry" ? await api.deleteRetexEntry(exerciseId, confirm.entry.id) : await api.clearRetex(exerciseId, confirm.eni);
      apply(next);
      notify({
        text: confirm.kind === "entry" ? t("Compte rendu retiré du RETEX") : t("RETEX de l'{eni} effacé", { eni: eniLabel(confirm.eni, data?.custom) }),
        tone: "info",
      });
      setConfirm(null);
    } catch (e) {
      notify({ text: t("Action impossible"), detail: (e as Error).message, tone: "error" });
    } finally {
      setBusy(false);
    }
  };

  const drop = useFileDrop(ready && !importing && Boolean(data) && !unavailable && !writeBlock.blocked, (file) =>
    openImport(file),
  );

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas" {...drop.handlers}>
      <ViewHeader
        view="retex"
        context={exercise?.name}
        badges={total > 0 ? <Badge>{total}</Badge> : null}
        actions={
          data &&
          !unavailable &&
          total > 0 && (
            <Button size="sm" onClick={() => openImport()} {...writeBlock}>
              <FileUp size={14} /> {t("Importer un compte rendu")}
            </Button>
          )
        }
      />

      <div className="relative min-h-0 flex-1 overflow-y-auto">
        {!ready ? (
          <DossierEmptyState view="retex" kind="none">
            {t("Le RETEX range les comptes rendus d'observation de l'ennemi : ceux de l'ennemi joué enrichissent la rédaction de la mise en ambiance.")}
          </DossierEmptyState>
        ) : query.isError ? (
          <EmptyState variant="error">{query.error.message}</EmptyState>
        ) : !data ? (
          <EmptyState variant="loading" title={t("Chargement du RETEX…")} />
        ) : unavailable ? (
          <EmptyState
            icon={NotebookPen}
            title={t("RETEX indisponible pour ce dossier.")}
            action={
              <Button highlight onClick={() => setView("objects")}>
                {t("Revenir à la carte")}
              </Button>
            }
          />
        ) : total === 0 ? (
          <EmptyState
            icon={NotebookPen}
            title={t("Aucun compte rendu au RETEX")}
            action={
              <Button onClick={() => openImport()} highlight={!writeBlock.blocked} {...writeBlock}>
                <FileUp size={14} /> {t("Importer un compte rendu")}
              </Button>
            }
            note={t("PDF, Word, LibreOffice, RTF, texte — ou texte collé. Vous pouvez aussi déposer le fichier ici.")}
          >
            <p>
              {t(
                "Importez un compte rendu d'observation de l'ennemi (RETEX d'exercice, fiche de renseignement) : il est découpé en passages, chacun classé par ennemi ; ceux qui décrivent un mode d'action ennemi sont proposés au RETEX de l'ennemi reconnu.",
              )}
            </p>
            {data.played && data.feeds_briefing && (
              <p className="mt-2">
                {t(
                  "Le RETEX de l'{eni}, ennemi du dossier, complétera sa description pour rédiger la situation géopolitique de la mise en ambiance.",
                  { eni: eniLabel(data.played, data.custom) },
                )}
              </p>
            )}
          </EmptyState>
        ) : (
          <div className="mx-auto w-full max-w-[860px] space-y-4 px-5 py-5 max-sm:px-4">
            {shown.map((group) => (
              <GroupCard
                key={group.eni}
                group={group}
                retex={data}
                onDelete={(entry) => setConfirm({ kind: "entry", entry })}
                onClear={() => setConfirm({ kind: "all", eni: group.eni })}
                onImport={() => openImport()}
              />
            ))}
            {empty.length > 0 && (
              <p className="px-1 text-meta text-ink-muted">
                {t("Sans RETEX dans ce dossier : {names}.", { names: empty.map((g) => eniLabel(g.eni, data.custom)).join(", ") })}
              </p>
            )}
          </div>
        )}
        <DropZone show={drop.dragging} label={t("Déposez le compte rendu pour le lire et le classer")} />
      </div>

      <Presence show={importing && Boolean(data) && !unavailable}>
        {importing && data && !unavailable && exerciseId && (
          <RetexImportDialog
            retex={data}
            initialFile={dropped}
            onClose={() => setImporting(false)}
            onAnalyze={({ file, text }) => (file ? api.analyzeRetexFile(exerciseId, file) : api.analyzeRetexText(exerciseId, text))}
            onAdd={async (eni, passages, source) => {
              const next = await api.addRetex(exerciseId, eni, passages, source);
              apply(next);
              setImporting(false);
              const group = next.enemies.find((g) => g.eni === eni);
              const feeds = Boolean(group?.played && next.feeds_briefing);
              notify({
                text: t("{reports} au RETEX de l'{eni}", {
                  reports: plural(next.added, t("compte rendu versé"), t("comptes rendus versés")),
                  eni: eniLabel(eni, next.custom),
                }),
                detail: [
                  feeds ? t("La situation géopolitique de la mise en ambiance en tiendra compte à sa prochaine rédaction.") : "",
                  next.dropped > 0
                    ? t("{removed} ({max} au plus).", {
                        removed: plural(next.dropped, t("compte rendu ancien est parti"), t("comptes rendus anciens sont partis")),
                        max: next.limits.entries,
                      })
                    : "",
                ]
                  .filter(Boolean)
                  .join(" "),
                tone: "ok",
                action: feeds ? { label: t("Mise en ambiance"), run: () => setView("briefing") } : undefined,
              });
            }}
          />
        )}
      </Presence>

      <Presence show={confirm !== null}>
        {confirm && (
          <ConfirmDialog
            title={confirm.kind === "entry" ? t("Retirer ce compte rendu ?") : t("Effacer le RETEX de l'{eni} ?", { eni: eniLabel(confirm.eni, data?.custom) })}
            confirmLabel={confirm.kind === "entry" ? t("Retirer") : t("Tout effacer")}
            busy={busy}
            busyLabel={confirm.kind === "entry" ? t("Retrait…") : t("Effacement…")}
            onConfirm={() => void remove()}
            onCancel={() => setConfirm(null)}
          >
            {confirm.kind === "entry"
              ? t("Le compte rendu « {source} » quittera le RETEX de ce dossier.", { source: tServer(confirm.entry.source) })
              : t("Tous les comptes rendus de cet ennemi quitteront le RETEX de ce dossier. Cette action est irréversible.")}
          </ConfirmDialog>
        )}
      </Presence>
    </div>
  );
}
